import React from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus, X, Copy, AlertTriangle } from "lucide-react";

const DAYS = [["mon", "Lun", "Lunedì"], ["tue", "Mar", "Martedì"], ["wed", "Mer", "Mercoledì"], ["thu", "Gio", "Giovedì"], ["fri", "Ven", "Venerdì"], ["sat", "Sab", "Sabato"], ["sun", "Dom", "Domenica"]];
const WEEK = ["mon", "tue", "wed", "thu", "fri"];
const make = (open, slots, extra = {}) => ({ ...Object.fromEntries(DAYS.map(([k]) => [k, open.includes(k) ? { open: true, slots: slots.map((s) => [...s]) } : { open: false, slots: [] }])), ...extra });
const PRESETS = [
  { id: "spezzato", label: "Lun–Sab 9–13 · 16–20", make: () => make([...WEEK, "sat"], [["09:00", "13:00"], ["16:00", "20:00"]]) },
  { id: "continuato", label: "Lun–Sab 9–19 continuato", make: () => make([...WEEK, "sat"], [["09:00", "19:00"]]) },
  { id: "sabato", label: "Lun–Ven 9–13 · 15–19, Sab mattina", make: () => make(WEEK, [["09:00", "13:00"], ["15:00", "19:00"]], { sat: { open: true, slots: [["09:00", "13:00"]] } }) },
  { id: "sempre", label: "Tutti i giorni 8–22", make: () => make(DAYS.map(([k]) => k), [["08:00", "22:00"]]) },
];
const slotKey = (d) => (d?.open && d.slots?.length ? d.slots.map((s) => s.join("–")).join(", ") : "chiuso");

export function summarizeHours(sch) {
  const out = [];
  for (let i = 0; i < DAYS.length;) {
    let j = i;
    while (j + 1 < DAYS.length && slotKey(sch[DAYS[j + 1][0]]) === slotKey(sch[DAYS[i][0]])) j += 1;
    out.push(`${DAYS[i][1]}${j > i ? `–${DAYS[j][1]}` : ""} ${slotKey(sch[DAYS[i][0]])}`);
    i = j + 1;
  }
  return out.join(" · ");
}

const timeCls = "h-8 rounded-md border border-input bg-background px-2 text-sm";

function DayRow({ k, label, d, onChange }) {
  const setSlot = (i, j, v) => onChange({ ...d, slots: d.slots.map((s, x) => (x === i ? (j === 0 ? [v, s[1]] : [s[0], v]) : s)) });
  const addBreak = () => { const [a, b] = d.slots[0]; onChange({ ...d, slots: [[a, "13:00"], ["15:30", b]] }); };
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 border-b border-border/60 last:border-0" data-testid={`hours-day-${k}`}>
      <span className="w-20 text-sm font-medium">{label}</span>
      <Switch checked={!!d.open} onCheckedChange={(v) => onChange({ open: v, slots: v && !d.slots.length ? [["09:00", "19:00"]] : d.slots })} aria-label={`${label} aperto`} data-testid={`hours-open-${k}`} />
      {!d.open && <span className="text-xs text-muted-foreground" data-testid={`hours-closed-${k}`}>Chiuso</span>}
      {d.open && d.slots.map((s, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <input type="time" step="900" className={timeCls} value={s[0]} onChange={(e) => setSlot(i, 0, e.target.value)} aria-label={`${label} apre`} data-testid={`hours-${k}-${i}-from`} />
          <span className="text-muted-foreground">–</span>
          <input type="time" step="900" className={timeCls} value={s[1]} onChange={(e) => setSlot(i, 1, e.target.value)} aria-label={`${label} chiude`} data-testid={`hours-${k}-${i}-to`} />
          {i > 0 && <button type="button" className="p-1 text-muted-foreground hover:text-red-500" onClick={() => onChange({ ...d, slots: d.slots.filter((_, x) => x !== i) })} aria-label="Togli la pausa" data-testid={`hours-${k}-${i}-remove`}><X className="w-3.5 h-3.5" /></button>}
        </span>
      ))}
      {d.open && d.slots.length === 1 && <button type="button" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" onClick={addBreak} data-testid={`hours-${k}-add-break`}><Plus className="w-3 h-3" /> pausa pranzo</button>}
    </div>
  );
}

export function OpeningHoursPicker({ value, onChange }) {
  const bad = value ? DAYS.filter(([k]) => value[k]?.open && value[k].slots.some(([a, b]) => !a || !b || a >= b)).map(([, , l]) => l) : [];
  const copyMonday = () => onChange({ ...value, ...Object.fromEntries(WEEK.slice(1).map((k) => [k, { open: value.mon.open, slots: value.mon.slots.map((s) => [...s]) }])) });
  return (
    <div className="space-y-3 compact-actions" data-testid="opening-hours-picker">
      <div className="flex flex-wrap gap-2" data-testid="hours-presets">
        {PRESETS.map((p) => <Button key={p.id} type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => onChange(p.make())} data-testid={`hours-preset-${p.id}`}>{p.label}</Button>)}
        {!value && <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => onChange(make([], []))} data-testid="hours-preset-custom">Imposto io giorno per giorno</Button>}
      </div>
      {!value && <p className="text-xs text-muted-foreground" data-testid="hours-empty">Scegli un modello: poi puoi ritoccare ogni giorno.</p>}
      {value && <>
        <div className="rounded-lg border border-border px-3">{DAYS.map(([k, , l]) => <DayRow key={k} k={k} label={l} d={value[k] || { open: false, slots: [] }} onChange={(d) => onChange({ ...value, [k]: d })} />)}</div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" onClick={copyMonday} data-testid="hours-copy-monday"><Copy className="w-3 h-3" /> Copia il lunedì da martedì a venerdì</button>
          <p className="text-xs text-muted-foreground" data-testid="hours-summary">I clienti vedranno: <strong className="text-foreground">{summarizeHours(value)}</strong></p>
        </div>
        {bad.length > 0 && <p className="text-xs text-amber-500 flex items-center gap-1" data-testid="hours-invalid"><AlertTriangle className="w-3.5 h-3.5" /> Controlla {bad.join(", ")}: l'apertura deve essere prima della chiusura.</p>}
      </>}
    </div>
  );
}
